import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useSelector } from "react-redux";

import { cloudFrontUrl } from "../nestserver";
import { selectUser } from "../redux/slices/auth.slice";

const SPLASH_MS = 1800;
const SEEN_KEY = "school-splash-seen-for";

function currentToken() {
  try {
    return window.localStorage.getItem("jwt-token");
  } catch (e) {
    return null;
  }
}

// Keyed on the JWT so it shows once per login (and per profile switch) and on a
// fresh browser session, but not on every in-app navigation or page reload.
function alreadyShown(token) {
  try {
    return window.sessionStorage.getItem(SEEN_KEY) === token;
  } catch (e) {
    return true;
  }
}

function markShown(token) {
  try {
    window.sessionStorage.setItem(SEEN_KEY, token);
  } catch (e) {
    // storage unavailable: the splash simply won't be remembered
  }
}

export default function SchoolSplash() {
  const school = useSelector(selectUser)?.school;
  const [visible, setVisible] = useState(() => {
    const token = currentToken();
    return Boolean(token && school?.logo && !alreadyShown(token));
  });

  useEffect(() => {
    if (!visible) return undefined;
    markShown(currentToken());
    const timer = setTimeout(() => setVisible(false), SPLASH_MS);
    return () => clearTimeout(timer);
  }, [visible]);

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          key="school-splash"
          initial={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.4 }}
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 99999,
            background: "#fff",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 24,
          }}
        >
          <motion.img
            initial={{ scale: 0.85, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.6 }}
            src={`${cloudFrontUrl}/common/${school.logo}`}
            alt={school.name || "School logo"}
            style={{ maxWidth: "45vw", maxHeight: "40vh", objectFit: "contain" }}
          />
          {school.name && (
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.5 }}
              style={{ fontSize: "1.5rem", fontWeight: 700, color: "#09617E" }}
            >
              {school.name}
            </motion.div>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
}
